import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { Activity, CRMUser, Lead, LeadSource, LeadStatus } from '../types';
import { LEAD_STATUSES_EXCLUDED_FROM_ALL } from '../components/leads/leadStatusDisplay';
import { formatSourceName } from './sourceUtils';

export type LeadLastUpdatedSource =
  | 'lead'
  | 'activity'
  | 'comment'
  | 'document'
  | 'deal'
  | 'engagement';

export interface LeadLastUpdatedInfo {
  at: Date;
  source: LeadLastUpdatedSource;
  label: string;
}

export const MISSING_TIMESTAMP_SENTINEL = new Date('2000-01-01T00:00:00Z');

export type LeadsDateRange = '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all';

export type LeadsSortColumn =
  | 'name'
  | 'status'
  | 'source'
  | 'location'
  | 'created'
  | 'lastUpdated'
  | 'addedBy';

export type LeadsFilterState = {
  searchQuery: string;
  statusFilter: LeadStatus | 'all';
  sourceFilter: LeadSource | 'all';
  dateRangeFilter: LeadsDateRange;
  showArchived: boolean;
  hotLeadsOnly: boolean;
  ownerFilter: string | 'all';
};

export function getDateCutoff(range: LeadsDateRange): Date | null {
  if (range === 'all') return null;
  const now = new Date();
  switch (range) {
    case '7d':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case '30d':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case '90d':
      return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    case '1y':
      return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    case 'ytd':
      return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
    default:
      return null;
  }
}

export function formatDateRangeLabel(range: LeadsDateRange): string {
  switch (range) {
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case '1y':
      return 'Last year';
    case 'ytd':
      return 'This calendar year';
    default:
      return 'All time';
  }
}

export function isValidTimestamp(date: Date): boolean {
  return date.getTime() !== MISSING_TIMESTAMP_SENTINEL.getTime();
}

export function formatDateSafe(date: Date, formatStr: string): string {
  if (!isValidTimestamp(date)) return 'Unknown';
  return format(date, formatStr);
}

export function getUserInitials(displayName: string | undefined, addedBy?: string): string {
  if (addedBy === 'WEB') return 'WEB';
  if (!displayName) return '—';
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}

export function buildNextActivityByLead(leads: Lead[], activities: Activity[]): Map<string, Activity | null> {
  const map = new Map<string, Activity | null>();

  leads.forEach(lead => {
    if (!lead.id) return;

    const leadActivities = activities.filter(
      a =>
        a.entityType === 'lead' &&
        a.entityId === lead.id &&
        a.status === 'scheduled' &&
        (a.scheduledAt || a.dueDate),
    );

    leadActivities.sort((a, b) => {
      const dateA = a.scheduledAt || a.dueDate;
      const dateB = b.scheduledAt || b.dueDate;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    map.set(lead.id, leadActivities[0] || null);
  });

  return map;
}

export function buildHasLoggedCallByLead(leads: Lead[], activities: Activity[]): Map<string, boolean> {
  const map = new Map<string, boolean>();

  leads.forEach(lead => {
    if (!lead.id) return;
    const hasCall = activities.some(
      a => a.entityType === 'lead' && a.entityId === lead.id && a.type === 'call' && a.status === 'completed',
    );
    map.set(lead.id, hasCall);
  });

  return map;
}

const HOT_SCORE_BANDS = new Set(['blazing', 'hot', 'warm']);

export function isHotLead(lead: Lead): boolean {
  const band = lead.enrichment?.score?.band;
  return !!band && HOT_SCORE_BANDS.has(band);
}

export function filterAndSortLeads(
  leads: Lead[],
  filters: LeadsFilterState,
  sortBy: LeadsSortColumn,
  sortOrder: 'asc' | 'desc',
  nextActivityByLead: Map<string, Activity | null>,
  lastUpdatedByLead: Map<string, LeadLastUpdatedInfo>,
  crmUsers: CRMUser[],
): Lead[] {
  let filtered = leads;

  if (!filters.showArchived) {
    filtered = filtered.filter(lead => !lead.archived);
  }

  if (filters.hotLeadsOnly) {
    filtered = filtered.filter(isHotLead);
  }

  if (filters.searchQuery.trim()) {
    const query = filters.searchQuery.toLowerCase().trim();
    filtered = filtered.filter(lead => {
      const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.toLowerCase();
      const email = (lead.email || '').toLowerCase();
      const phone = (lead.phone ? String(lead.phone) : '').replace(/\D/g, '');
      const queryDigits = query.replace(/\D/g, '');
      const company = (lead.company || '').toLowerCase();
      const location = (lead.location || '').toLowerCase();
      const source = (lead.source || '').toLowerCase();
      const labels = (lead.labels || []).join(' ').toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        (queryDigits.length >= 3 && phone.includes(queryDigits)) ||
        company.includes(query) ||
        location.includes(query) ||
        source.includes(query) ||
        labels.includes(query)
      );
    });
  }

  if (filters.statusFilter !== 'all') {
    filtered = filtered.filter(lead => lead.status === filters.statusFilter);
  } else {
    filtered = filtered.filter(
      lead => !LEAD_STATUSES_EXCLUDED_FROM_ALL.includes(lead.status),
    );
  }

  if (filters.sourceFilter !== 'all') {
    filtered = filtered.filter(lead => {
      const leadSource = formatSourceName(lead.source || 'unspecified');
      const filterSource = formatSourceName(filters.sourceFilter);
      return leadSource === filterSource;
    });
  }

  if (filters.ownerFilter !== 'all') {
    filtered = filtered.filter(lead => (lead.addedBy || '') === filters.ownerFilter);
  }

  const dateCutoff = getDateCutoff(filters.dateRangeFilter);
  if (dateCutoff) {
    filtered = filtered.filter(lead => {
      const createdAt = lead.createdAt instanceof Date ? lead.createdAt : new Date(lead.createdAt);
      return createdAt >= dateCutoff;
    });
  }

  return [...filtered].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        break;
      case 'location':
        comparison = (a.location || '').localeCompare(b.location || '');
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'source':
        comparison = a.source.localeCompare(b.source);
        break;
      case 'created':
        comparison = a.createdAt.getTime() - b.createdAt.getTime();
        break;
      case 'lastUpdated': {
        const dateA = a.id ? lastUpdatedByLead.get(a.id)?.at : null;
        const dateB = b.id ? lastUpdatedByLead.get(b.id)?.at : null;
        if (!dateA && !dateB) comparison = 0;
        else if (!dateA) comparison = 1;
        else if (!dateB) comparison = -1;
        else comparison = dateA.getTime() - dateB.getTime();
        break;
      }
      case 'addedBy': {
        const userA = crmUsers.find(u => u.uid === a.addedBy);
        const userB = crmUsers.find(u => u.uid === b.addedBy);
        comparison = (userA?.displayName || '').localeCompare(userB?.displayName || '');
        break;
      }
    }

    comparison = sortOrder === 'asc' ? comparison : -comparison;
    if (comparison === 0) {
      comparison = (a.id || '').localeCompare(b.id || '');
    }
    return comparison;
  });
}

export function formatLastUpdated(info: LeadLastUpdatedInfo | null | undefined): string {
  if (!info?.at || !isValidTimestamp(info.at)) return '—';
  const d = info.at;
  if (isToday(d)) return formatDistanceToNow(d, { addSuffix: true });
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMM d, yyyy');
}

export function countLeadsNeedingFollowUp(
  leads: Lead[],
  nextActivityByLead: Map<string, Activity | null>,
): number {
  const now = Date.now();
  return leads.filter(lead => {
    if (lead.archived || lead.status === 'lost') return false;
    const next = lead.id ? nextActivityByLead.get(lead.id) : null;
    if (!next) return lead.status === 'new';
    const date = next.scheduledAt || next.dueDate;
    if (!date) return false;
    return new Date(date).getTime() <= now;
  }).length;
}
